import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  LinearProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import apiClient from "@/api/client";

type WindowDays = 30 | 90 | 365;

interface SourceStat {
  source: string;
  leads: number;
  booked_audits: number;
  won_deals: number;
  booking_rate: number | null;
  win_rate: number | null;
}

interface LeadsBySourceResponse {
  window_days: number;
  sources: SourceStat[];
}

interface DropOffResponse {
  window_days: number;
  leads: number;
  contacted: number;
  audit_booked: number;
  won: number;
  drop_offs: {
    no_contact: number;
    contacted_no_booking: number;
    booked_no_win: number;
  };
}

interface ContentEffectivenessResponse {
  window_days: number;
  caveat: string;
  posts: { post_title: string; published_at: string; leads_within_14d: number }[];
}

interface WeeklySummaryResponse {
  this_week_leads: number;
  trailing_weekly_avg: number;
  delta_vs_avg: number;
  top_converting_source_90d: {
    source: string;
    leads: number;
    booked_audits: number;
    booking_rate: number;
  } | null;
}

function pct(n: number | null): string {
  if (n === null) return "—";
  return `${(n * 100).toFixed(0)}%`;
}

export default function Insights() {
  const [windowDays, setWindowDays] = useState<WindowDays>(90);

  const { data: bySource, isLoading: loadingSource } = useQuery<LeadsBySourceResponse>({
    queryKey: ["insights-leads-by-source", windowDays],
    queryFn: async () => {
      const res = await apiClient.get<LeadsBySourceResponse>(
        `/insights/leads-by-source?days=${windowDays}`,
      );
      return res.data;
    },
  });

  const { data: dropOff } = useQuery<DropOffResponse>({
    queryKey: ["insights-drop-off", windowDays],
    queryFn: async () => {
      const res = await apiClient.get<DropOffResponse>(
        `/insights/linkedin-drop-off?days=${windowDays}`,
      );
      return res.data;
    },
  });

  const { data: content } = useQuery<ContentEffectivenessResponse>({
    queryKey: ["insights-content", windowDays],
    queryFn: async () => {
      const res = await apiClient.get<ContentEffectivenessResponse>(
        `/insights/content-effectiveness?days=${windowDays}`,
      );
      return res.data;
    },
  });

  const { data: weekly } = useQuery<WeeklySummaryResponse>({
    queryKey: ["insights-weekly-summary"],
    queryFn: async () => {
      const res = await apiClient.get<WeeklySummaryResponse>("/insights/weekly-summary");
      return res.data;
    },
  });

  const totalLeads = bySource ? bySource.sources.reduce((s, r) => s + r.leads, 0) : 0;

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Insights
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Source attribution + LinkedIn funnel + content effectiveness. Use this to decide what's
        actually converting and double down on it.
      </Typography>

      <Stack direction="row" spacing={1} sx={{ mb: 3 }}>
        {([30, 90, 365] as const).map((d) => (
          <Chip
            key={d}
            label={`Last ${d} days`}
            variant={windowDays === d ? "filled" : "outlined"}
            color={windowDays === d ? "primary" : "default"}
            onClick={() => setWindowDays(d)}
          />
        ))}
      </Stack>

      {weekly && (
        <Alert severity={weekly.delta_vs_avg >= 0 ? "success" : "info"} sx={{ mb: 3 }}>
          <strong>This week:</strong> {weekly.this_week_leads} lead(s) ({" "}
          {weekly.delta_vs_avg >= 0 ? "+" : ""}
          {weekly.delta_vs_avg.toFixed(1)} vs trailing weekly avg of {weekly.trailing_weekly_avg}).
          {weekly.top_converting_source_90d && (
            <>
              {" Top-converting source (rolling 90d): "}
              <strong>{weekly.top_converting_source_90d.source}</strong> at{" "}
              {pct(weekly.top_converting_source_90d.booking_rate)} booking rate.
            </>
          )}
        </Alert>
      )}

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 2 }}>
        {/* Leads by source */}
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Leads by source
            </Typography>
            {loadingSource && <CircularProgress size={20} />}
            {bySource && bySource.sources.length === 0 && (
              <Alert severity="info">No leads in this window yet.</Alert>
            )}
            {bySource && bySource.sources.length > 0 && (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Source</TableCell>
                    <TableCell align="right">Leads</TableCell>
                    <TableCell align="right">Booked</TableCell>
                    <TableCell align="right">Won</TableCell>
                    <TableCell align="right">Book %</TableCell>
                    <TableCell align="right">Win %</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {bySource.sources.map((r) => (
                    <TableRow key={r.source}>
                      <TableCell>{r.source}</TableCell>
                      <TableCell align="right">{r.leads}</TableCell>
                      <TableCell align="right">{r.booked_audits}</TableCell>
                      <TableCell align="right">{r.won_deals}</TableCell>
                      <TableCell align="right">{pct(r.booking_rate)}</TableCell>
                      <TableCell align="right">{pct(r.win_rate)}</TableCell>
                    </TableRow>
                  ))}
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Total</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>
                      {totalLeads}
                    </TableCell>
                    <TableCell colSpan={4} />
                  </TableRow>
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* LinkedIn drop-off */}
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              LinkedIn funnel
            </Typography>
            {dropOff && dropOff.leads === 0 ? (
              <Alert severity="info">No LinkedIn leads in this window yet.</Alert>
            ) : (
              dropOff && (
                <Stack spacing={1.5}>
                  <FunnelRow label="Captured" value={dropOff.leads} total={dropOff.leads} />
                  <FunnelRow
                    label="Contacted (any comm)"
                    value={dropOff.contacted}
                    total={dropOff.leads}
                  />
                  <FunnelRow
                    label="Audit booked / proposal+"
                    value={dropOff.audit_booked}
                    total={dropOff.leads}
                  />
                  <FunnelRow label="Won" value={dropOff.won} total={dropOff.leads} />
                  <Typography variant="caption" color="text.secondary">
                    Drop-offs — no contact: {dropOff.drop_offs.no_contact} · contacted no booking:{" "}
                    {dropOff.drop_offs.contacted_no_booking} · booked no win:{" "}
                    {dropOff.drop_offs.booked_no_win}
                  </Typography>
                </Stack>
              )
            )}
          </CardContent>
        </Card>

        {/* Content effectiveness */}
        <Card sx={{ gridColumn: { md: "span 2" } }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Content effectiveness
            </Typography>
            {content && (
              <Alert severity="info" sx={{ mb: 2, fontSize: "0.85rem" }}>
                {content.caveat}
              </Alert>
            )}
            {content && content.posts.length === 0 && (
              <Alert severity="info">No published LinkedIn posts in this window yet.</Alert>
            )}
            {content && content.posts.length > 0 && (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Post</TableCell>
                    <TableCell>Published</TableCell>
                    <TableCell align="right">Leads in 14d</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {content.posts.map((p, idx) => (
                    <TableRow key={`${p.post_title}-${idx}`}>
                      <TableCell>{p.post_title}</TableCell>
                      <TableCell>{new Date(p.published_at).toLocaleDateString()}</TableCell>
                      <TableCell align="right">{p.leads_within_14d}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
}

function FunnelRow({ label, value, total }: { label: string; value: number; total: number }) {
  const pctVal = total > 0 ? (value / total) * 100 : 0;
  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.25 }}>
        <Typography variant="body2">{label}</Typography>
        <Typography variant="body2" fontWeight={600}>
          {value} ({pctVal.toFixed(0)}%)
        </Typography>
      </Box>
      <LinearProgress variant="determinate" value={pctVal} sx={{ height: 6, borderRadius: 1 }} />
    </Box>
  );
}
